import { ApiRequestError } from '@/api/client';
import { http } from '@/api/http';

export type MyStatisticsOrder = {
  id: string;
  teamName: string;
  address: string;
  serviceDate: string;
  hours: number;
  commission: number | null;
  /** 订单状态文案，如未结算时佣金为 0 */
  statusText: string;
};

export type MyStatisticsResult = {
  items: MyStatisticsOrder[];
  totalHours: number;
  totalCommission: number;
  orderCount: number;
  page: number;
  pageSize: number;
  total: number;
};

export type FetchMyStatisticsParams = {
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
  /** 不传则统计当前账号所属的全部团队。 */
  teamId?: number;
};

export type FetchMemberWorkStatisticsParams = FetchMyStatisticsParams & {
  /** 不传则统计当前团队全部服务人员。 */
  operatorId?: number;
};

const LIST_KEYS = ['items', 'list', 'orders', 'records', 'rows', 'data'] as const;

function readString(o: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = o[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return '';
}

function readNumber(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const value = o[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readList(payload: Record<string, unknown>): unknown[] {
  const orders = payload.orders;
  if (Array.isArray(orders) && orders.length > 0) return orders;
  for (const key of LIST_KEYS) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function nestedOrderRecord(row: Record<string, unknown>): Record<string, unknown> | null {
  return asRecord(row.order)
    ?? asRecord(row.orders)
    ?? asRecord(row.order_info)
    ?? asRecord(row.orderInfo);
}

/** 单笔工时以订单 `service_hours` 为准，避免读到外层为 0 的 duration/hours。 */
function readServiceHours(row: Record<string, unknown>): number {
  const sources = [row, nestedOrderRecord(row)].filter(
    (source): source is Record<string, unknown> => source !== null,
  );
  for (const source of sources) {
    const hours = readNumber(source, ['service_hours', 'serviceHours']);
    if (hours !== undefined && hours > 0) return hours;
  }
  for (const source of sources) {
    const hours = readNumber(source, ['service_hours', 'serviceHours']);
    if (hours !== undefined) return hours;
  }
  return readNumber(row, [
    'work_hours',
    'workHours',
    'hours',
    'service_duration',
    'serviceDuration',
    'duration',
  ]) ?? 0;
}

const ORDER_STATUS_LABELS: Record<number, string> = {
  0: 'Pending',
  100: 'Pending',
  1: 'In Progress',
  2: 'Service Done',
  3: 'Completed',
  6: 'Completed',
  4: 'Cancelled',
};

const STATUS_TEXT_EN: Record<string, string> = {
  待派发: 'Pending',
  等待派发: 'Pending',
  服务中: 'In Progress',
  进行中: 'In Progress',
  服务完成: 'Service Done',
  已结账: 'Completed',
  已结算: 'Completed',
  已完成: 'Completed',
  已评价: 'Completed',
  已取消: 'Cancelled',
};

function toEnglishStatusText(text: string, status?: number): string {
  if (status !== undefined && ORDER_STATUS_LABELS[status]) return ORDER_STATUS_LABELS[status];
  const mapped = STATUS_TEXT_EN[text];
  if (mapped) return mapped;
  if (/[\u4e00-\u9fff]/.test(text)) return '';
  return text;
}

function readStatusText(row: Record<string, unknown>): string {
  const nested = nestedOrderRecord(row);
  const status = readNumber(row, ['status', 'order_status', 'orderStatus'])
    ?? (nested ? readNumber(nested, ['status', 'order_status', 'orderStatus']) : undefined);
  const text = readString(row, ['status_text', 'statusText'])
    || (nested ? readString(nested, ['status_text', 'statusText']) : '');
  return toEnglishStatusText(text, status);
}

function parseOrder(raw: unknown, index: number): MyStatisticsOrder | null {
  const o = asRecord(raw);
  if (!o) return null;
  const teamName = readString(o, ['team_name', 'teamName', 'team', 'belong_team', 'belongTeam']);
  const address = readString(o, [
    'address',
    'order_address',
    'orderAddress',
    'service_address',
    'serviceAddress',
    'full_address',
    'fullAddress',
  ]);
  const serviceDate = readString(o, [
    'service_date',
    'serviceDate',
    'service_time',
    'serviceTime',
    'date',
    'work_date',
    'workDate',
  ]);
  const hours = readServiceHours(o);
  const statusText = readStatusText(o);
  const commission = readNumber(o, [
    'commission',
    'commission_amount',
    'commissionAmount',
    'amount',
    'wage',
    'labor_cost',
    'laborCost',
  ]) ?? null;
  if (!teamName && !address && !serviceDate && hours <= 0 && (commission == null || commission <= 0) && !statusText) {
    return null;
  }
  const id = readString(o, ['id', 'order_id', 'orderId']) || `${teamName}-${serviceDate}-${index}`;
  return {
    id,
    teamName: teamName || 'Team',
    address: address || '—',
    serviceDate,
    hours,
    commission,
    statusText,
  };
}

function readSummary(payload: Record<string, unknown>): {
  totalHours?: number;
  totalCommission?: number;
  orderCount?: number;
} {
  const summary = asRecord(payload.summary)
    ?? asRecord(payload.statistics)
    ?? asRecord(payload.total_info)
    ?? asRecord(payload.totalInfo)
    ?? payload;
  return {
    totalHours: readNumber(summary, [
      'total_hours',
      'totalHours',
      'total_duration',
      'totalDuration',
      'total_work_hours',
      'totalWorkHours',
    ]),
    totalCommission: readNumber(summary, [
      'total_commission',
      'totalCommission',
      'total_amount',
      'totalAmount',
      'commission_total',
      'commissionTotal',
    ]),
    orderCount: readNumber(summary, ['order_count', 'orderCount', 'total_orders', 'totalOrders', 'count']),
  };
}

function readTotal(payload: Record<string, unknown>, fallback: number): number {
  const pagination = asRecord(payload.pagination);
  const fromPage = pagination
    ? readNumber(pagination, ['total_count', 'totalCount', 'total'])
    : undefined;
  return fromPage ?? readNumber(payload, ['total', 'total_count', 'totalCount']) ?? fallback;
}

/** 解析工时统计 `data`。个人与团队看板的列表结构一致。 */
export function parseMyStatistics(data: unknown, page: number, pageSize: number): MyStatisticsResult {
  if (Array.isArray(data)) {
    const items = data
      .map(parseOrder)
      .filter((item): item is MyStatisticsOrder => item !== null);
    return {
      items,
      totalHours: items.reduce((sum, item) => sum + item.hours, 0),
      totalCommission: items.reduce((sum, item) => sum + (item.commission ?? 0), 0),
      orderCount: items.length,
      page,
      pageSize,
      total: items.length,
    };
  }
  const payload = asRecord(data) ?? {};
  const items = readList(payload)
    .map(parseOrder)
    .filter((item): item is MyStatisticsOrder => item !== null);
  const summary = readSummary(payload);
  const summedHours = items.reduce((sum, item) => sum + item.hours, 0);
  const summedCommission = items.reduce((sum, item) => sum + (item.commission ?? 0), 0);
  const totalHours = summary.totalHours && summary.totalHours > 0
    ? summary.totalHours
    : summedHours;
  return {
    items,
    totalHours,
    totalCommission: summary.totalCommission ?? summedCommission,
    orderCount: summary.orderCount ?? readTotal(payload, items.length),
    page: readNumber(payload, ['page']) ?? page,
    pageSize: readNumber(payload, ['page_size', 'pageSize']) ?? pageSize,
    total: readTotal(payload, summary.orderCount ?? items.length),
  };
}

function statisticsBody(params: FetchMyStatisticsParams): Record<string, string | number> {
  const page = params.page && params.page > 0 ? params.page : 1;
  const pageSize = params.pageSize && params.pageSize > 0 ? params.pageSize : 20;
  const body: Record<string, string | number> = {
    page,
    page_size: pageSize,
  };
  const startDate = params.startDate?.trim();
  const endDate = params.endDate?.trim();
  if (startDate) body.start_date = startDate;
  if (endDate) body.end_date = endDate;
  return body;
}

/** POST `/system/team/op/my_statistics` — 当前账号自己的工单工时与佣金。 */
export async function fetchMyStatistics(
  params: FetchMyStatisticsParams = {},
): Promise<MyStatisticsResult> {
  const body = statisticsBody(params);
  if (params.teamId != null && Number.isInteger(params.teamId)) {
    body.team_id = params.teamId;
  }
  const res = await http.post<{ error?: boolean; message?: string; data?: unknown }>(
    '/system/team/op/my_statistics',
    body,
    { skipApiEnvelopeModal: true },
  );
  if (res.error === true) {
    throw new ApiRequestError(
      typeof res.message === 'string' ? res.message : 'Failed to load work statistics.',
    );
  }
  return parseMyStatistics(res.data, Number(body.page), Number(body.page_size));
}

/** POST `/system/team/order/member_work_statistics` — 当前团队服务人员工时与佣金。 */
export async function fetchMemberWorkStatistics(
  params: FetchMemberWorkStatisticsParams = {},
): Promise<MyStatisticsResult> {
  const body = statisticsBody(params);
  if (params.operatorId != null && Number.isInteger(params.operatorId)) {
    body.operator_id = params.operatorId;
  }

  const res = await http.post<{ error?: boolean; message?: string; data?: unknown }>(
    '/system/team/order/member_work_statistics',
    body,
    { skipApiEnvelopeModal: true },
  );
  if (res.error === true) {
    throw new ApiRequestError(
      typeof res.message === 'string' ? res.message : 'Failed to load work statistics.',
    );
  }
  return parseMyStatistics(res.data, Number(body.page), Number(body.page_size));
}
